import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addLead, listLocalityByCity } from "../../Redux/Actions/leadActions";
import { viewCountryListPagination, viewAllCity } from "../../Redux/Actions/settingsActions";
import { agentProfitRangeList } from "../../Redux/Actions/agentProfitRangeActions";
import { getProjectList } from "../../Redux/Actions/projectActions";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";
import toast from "react-hot-toast";
import "./addLead.css";

const libraries = ["places"];

const mapContainerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "10px",
    marginTop: "10px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };

const LEAD_SOURCES = [
    { value: "other", label: "Other" },
    { value: "partner", label: "Partner" },
    { value: "meta_ads", label: "Meta Ads" },
    { value: "google_ads", label: "Google Ads" },
    { value: "user_app", label: "User App" },
];

const LEAD_FOR = [
    { value: "home_visit", label: "Home Visit" },
    { value: "Site_visit", label: "Site Visit" },
];

const SITE_VISIT_BY = [
    { value: "self", label: "Self" },
    { value: "byagent", label: "By Agent" },
];

const getMinAppointmentDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
};

const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    let [hour, minute] = time24.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const emptyAddress = {
    address: "",
    appartment_road_area: "",
    business_type: "",
    direction_to_reach: "",
    house_flat_floorno: "",
};

const AddLead = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const autocompleteRef = useRef(null);
    const localityRef = useRef(null);
    const projectRef = useRef(null);
    const [localityOpen, setLocalityOpen] = useState(false);
    const [projectOpen, setProjectOpen] = useState(false);
    const [projectSearch, setProjectSearch] = useState("");

    const { countryList } = useSelector((state) => state.country);
    const { citiesList } = useSelector((state) => state.city || {});
    const { agentProfitRanges } = useSelector((state) => state.agentProfitRange || {});
    const { localityList } = useSelector((state) => state.leads || {});
    const { projects: projectListData } = useSelector((state) => state.projects || {});

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries,
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [noteInput, setNoteInput] = useState("");

    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [markerPosition, setMarkerPosition] = useState(null);

    const [leadData, setLeadData] = useState({
        name: "",
        email: "",
        country_code: "+91",
        contact_number: "",
        country: "",
        city: "",
        locality: [],
        project_id: [],
        address: { ...emptyAddress },
        lat: 0,
        lng: 0,
        price_range: "",
        lead_source: "other",
        appointment_date: "",
        slot_start_time: "",
        slot_end_time: "",
        lead_for: "home_visit",
        site_visit_by: "self",
        site_visit_purpose: "",
        note: [],
    });

    const prevCityRef = useRef(leadData.city);

    useEffect(() => {
        dispatch(viewCountryListPagination());
        dispatch(viewAllCity());
        dispatch(agentProfitRangeList({ page: 1, limit: 100 }));
    }, [dispatch]);

    useEffect(() => {
        if (leadData.city) {
            dispatch(listLocalityByCity({ city_id: leadData.city }));
            setLeadData((prev) => ({ ...prev, locality: [] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadData.city]);

    // Fetch projects only once both city and locality are selected
    useEffect(() => {
        if (!leadData.city || !leadData.locality.length) return;

        const projectPayload = {
            page: 1,
            limit: 100,
            is_deleted: false,
            city: leadData.city,
            localities: leadData.locality,
            status: ["published"],
        };

        dispatch(getProjectList(projectPayload, false));
        setLeadData((prev) => ({ ...prev, project_id: [] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadData.city, leadData.locality]);


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (localityRef.current && !localityRef.current.contains(e.target)) {
                setLocalityOpen(false);
            }
            if (projectRef.current && !projectRef.current.contains(e.target)) {
                setProjectOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const groupedProjects = useMemo(() => {
        const map = {};
        leadData.locality.forEach((locId) => {
            const meta = (localityList?.data || []).find((l) => l._id === locId);
            map[locId] = { id: locId, name: meta?.name || "Unknown locality", projects: [] };
        });

        (projectListData?.data || []).forEach((proj) => {
            const projLocalityIds = (proj.localities || []).map((l) => l._id);
            leadData.locality.forEach((locId) => {
                if (projLocalityIds.includes(locId)) {
                    map[locId].projects.push(proj);
                }
            });
        });

        return Object.values(map).filter((group) => group.projects.length > 0);
    }, [projectListData, leadData.locality, localityList]);

    const toggleProject = (id) => {
        setLeadData((prev) => {
            const exists = prev.project_id.includes(id);
            return {
                ...prev,
                project_id: exists ? prev.project_id.filter((p) => p !== id) : [...prev.project_id, id],
            };
        });
        clearError("project_id");
    };

    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLeadData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const handleLocalityChange = (values) => {
        setLeadData((prev) => ({ ...prev, locality: values }));
        clearError("locality");
    };

    const handleProjectChange = (values) => {
        setLeadData((prev) => ({ ...prev, project_id: values }));
        clearError("project_id");
    };

    // Handles the sub-fields inside leadData.address (address, appartment_road_area, etc.)
    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setLeadData((prev) => ({
            ...prev,
            address: { ...prev.address, [name]: value },
        }));
        if (name === "address") clearError("address");
    };

    const onAutocompleteLoad = (ac) => {
        autocompleteRef.current = ac;
    };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setMarkerPosition({ lat, lng });
            setMapCenter({ lat, lng });
            setLeadData((prev) => ({
                ...prev,
                address: {
                    ...prev.address,
                    address: place.formatted_address || prev.address.address,
                },
                lat,
                lng,
            }));
            clearError("address");
        }
    };

    const onMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });
        setLeadData((prev) => ({ ...prev, lat, lng }));

        if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === "OK" && results[0]) {
                    setLeadData((prev) => ({
                        ...prev,
                        address: {
                            ...prev.address,
                            address: results[0].formatted_address,
                        },
                    }));
                    clearError("address");
                }
            });
        }
    }, []);

    const handleAddNote = () => {
        const val = noteInput.trim();
        if (!val) return;
        setLeadData((prev) => ({ ...prev, note: [...prev.note, val] }));
        setNoteInput("");
    };

    const handleNoteKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddNote();
        }
    };

    const handleRemoveNote = (idx) => {
        setLeadData((prev) => ({
            ...prev,
            note: prev.note.filter((_, i) => i !== idx),
        }));
    };

    const handleReset = () => {
        setLeadData({
            name: "",
            email: "",
            country_code: "+91",
            contact_number: "",
            country: "",
            city: "",
            address: { ...emptyAddress },
            locality: [],
            project_id: [],
            lat: 0,
            lng: 0,
            price_range: "",
            lead_source: "other",
            appointment_date: "",
            slot_start_time: "",
            slot_end_time: "",
            lead_for: "home_visit",
            site_visit_by: "self",
            site_visit_purpose: "",
            note: [],
        });
        setNoteInput("");
        setErrors({});
        setMarkerPosition(null);
        setMapCenter(defaultCenter);
    };

    const validate = () => {
        const newErrors = {};
        if (!leadData.name.trim()) newErrors.name = "Name is required";
        if (!leadData.contact_number.trim()) newErrors.contact_number = "Contact number is required";
        if (!leadData.country) newErrors.country = "Country is required";
        if (!leadData.city) newErrors.city = "City is required";
        if (leadData.lead_for !== "Site_visit" && !leadData.address.address.trim()) {
            newErrors.address = "Address is required";
        }
        if (!leadData.locality.length) {
            newErrors.locality = "At least one locality is required";
        }
        if (leadData.lead_for === "Site_visit" && !leadData.project_id.length) {
            newErrors.project_id = "At least one project is required";
        }
        if (!leadData.lead_source) newErrors.lead_source = "Lead source is required";
        if (!leadData.price_range) newErrors.price_range = "Price range is required";
        if (!leadData.appointment_date) newErrors.appointment_date = "Appointment date is required";
        if (!leadData.slot_start_time) newErrors.slot_start_time = "Start time is required";
        if (!leadData.slot_end_time) newErrors.slot_end_time = "End time is required";
        if (
            leadData.slot_start_time &&
            leadData.slot_end_time &&
            leadData.slot_start_time >= leadData.slot_end_time
        ) newErrors.slot_end_time = "End time must be after start time";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                name: leadData.name,
                country_code: leadData.country_code,
                contact_number: leadData.contact_number,
                country: leadData.country,
                city: leadData.city,
                address: leadData.address,
                price_range: leadData.price_range,
                lead_source: leadData.lead_source,
                locality: leadData.locality,
                project_id: leadData.project_id,
                lead_for: leadData.lead_for,
                appointment_date: leadData.appointment_date,
                time_slot: `${formatTimeTo12Hour(leadData.slot_start_time)} to ${formatTimeTo12Hour(leadData.slot_end_time)}`,
            };

            if (leadData.email) payload.email = leadData.email;
            if (leadData.note.length) payload.note = leadData.note;

            if (leadData.lat && leadData.lng) {
                payload.location = {
                    type: "Point",
                    coordinates: [leadData.lng, leadData.lat],
                };
            }

            if (leadData.lead_for === "Site_visit") {
                payload.pickup_address = leadData.address.address;
                payload.pickup_date_time = new Date(`${leadData.appointment_date}T${leadData.slot_start_time}`);
                payload.site_visit_by = leadData.site_visit_by;
                if (leadData.site_visit_purpose) payload.site_visit_purpose = leadData.site_visit_purpose;
            }

            const response = await dispatch(addLead(payload));

            console.log("response",response)

            if (response?.statusCode === 200) {
                toast.success("Lead added successfully!");
                handleReset();
                navigate("/leads");
            } else {
                toast.error(response?.message || "Failed to add lead");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Failed to add lead");
        } finally {
            setLoading(false);
        }
    };

    const formatIndianPrice = (value) => {
        if (value >= 10000000) return `₹${(value / 10000000).toFixed(value % 10000000 === 0 ? 0 : 2)}Cr`;
        if (value >= 100000) return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 2)}L`;
        return `₹${value.toLocaleString("en-IN")}`;
    };

    return (
        <MainLayout>
            <div className="al-wrapper">
                <div className="al-header">
                    <div className="al-header-left">
                        <div className="al-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="al-title">Add Lead</h1>
                            <p className="al-subtitle">Create a new lead manually</p>
                        </div>
                    </div>

                    <Link to="/leads" className="al-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 21h18" />
                            <path d="M5 21V7l7-4 7 4v14" />
                        </svg>
                        View Leads
                    </Link>
                </div>

                <form className="al-form" onSubmit={handleSubmit}>
                    {/* ── Section 00: Lead For ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">00</span>
                            <h2 className="al-section-title">Lead For</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Lead For <span className="al-req">*</span></label>
                                <select
                                    className="al-select"
                                    name="lead_for"
                                    value={leadData.lead_for}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    {LEAD_FOR.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>

                            {leadData.lead_for === "Site_visit" && (
                                <div className="al-field">
                                    <label className="al-label">
                                        Visit By <span className="al-req">*</span>
                                    </label>

                                    <select
                                        className="al-select"
                                        name="site_visit_by"
                                        value={leadData.site_visit_by}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {SITE_VISIT_BY.map((s) => (
                                            <option key={s.value} value={s.value}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Section 01: Basic Info ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">01</span>
                            <h2 className="al-section-title">Basic Information</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Name <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.name ? "al-input--error" : ""}`}
                                    type="text"
                                    name="name"
                                    value={leadData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Rahul Sharma"
                                    disabled={loading}
                                />
                                {errors.name && <span className="al-error-msg">{errors.name}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Email <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="email"
                                    name="email"
                                    value={leadData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. rahul@example.com"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Country Code</label>
                                <select
                                    className="al-select"
                                    name="country_code"
                                    value={leadData.country_code}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="+91">India (+91)</option>
                                    <option value="+1">USA (+1)</option>
                                    <option value="+971">UAE (+971)</option>
                                </select>
                            </div>

                            <div className="al-field">
                                <label className="al-label">Contact Number <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.contact_number ? "al-input--error" : ""}`}
                                    type="text"
                                    name="contact_number"
                                    value={leadData.contact_number}
                                    onChange={handleChange}
                                    placeholder="e.g. 9876543211"
                                    disabled={loading}
                                />
                                {errors.contact_number && <span className="al-error-msg">{errors.contact_number}</span>}
                            </div>

                            {leadData.lead_for === "Site_visit" && (
                                <div className="al-field">
                                    <label className="al-label">Site Visit Purpose <span className="al-optional">(Optional)</span></label>
                                    <input
                                        className="al-input"
                                        type="text"
                                        name="site_visit_purpose"
                                        value={leadData.site_visit_purpose}
                                        onChange={handleChange}
                                        placeholder="e.g. Property inspection"
                                        disabled={loading}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Section 02: Location ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">02</span>
                            <h2 className="al-section-title">Location</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Country <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.country ? "al-input--error" : ""}`}
                                    name="country"
                                    value={leadData.country}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select country</option>
                                    {countryList?.data?.map((country) => (
                                        <option key={country._id} value={country._id}>
                                            {country.country_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.country && <span className="al-error-msg">{errors.country}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">City <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.city ? "al-input--error" : ""}`}
                                    name="city"
                                    value={leadData.city}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select city</option>
                                    {citiesList?.data?.map((city) => (
                                        <option key={city._id} value={city._id}>
                                            {city.city_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.city && <span className="al-error-msg">{errors.city}</span>}
                            </div>

                            <SearchableMultiSelect
                                label="Locality"
                                required={true}
                                placeholder={!leadData.city ? "Select city first" : "Search localities..."}
                                disabled={!leadData.city || loading}
                                options={(localityList?.data || []).map((loc) => ({
                                    value: loc._id,
                                    label: loc.name,
                                }))}
                                selected={leadData.locality}
                                onChange={handleLocalityChange}
                                error={errors.locality}
                                emptyMessage="Select a city first"
                            />

                            <div className="al-field al-field--full" ref={projectRef}>
                                <label className="al-label">
                                    {leadData.lead_for === "Site_visit" ? "Project" : "Project (Optional)"}
                                    {leadData.lead_for === "Site_visit" && <span className="al-req">*</span>}
                                </label>

                                <div className="al-dropdown">
                                    <button
                                        type="button"
                                        className="al-dropdown-toggle"
                                        onClick={() => setProjectOpen((o) => !o)}
                                        disabled={!leadData.city || !leadData.locality.length || loading}
                                    >
                                        <span>
                                            {!leadData.city
                                                ? "Select city first"
                                                : !leadData.locality.length
                                                    ? "Select locality first"
                                                    : leadData.project_id.length
                                                        ? `${leadData.project_id.length} project(s) selected`
                                                        : "Search projects..."}
                                        </span>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M6 9l6 6 6-6" />
                                        </svg>
                                    </button>

                                    {projectOpen && (
                                        <div className="al-dropdown-menu">
                                            <input
                                                type="text"
                                                className="al-input"
                                                style={{ marginBottom: 8 }}
                                                placeholder="Filter projects..."
                                                value={projectSearch}
                                                onChange={(e) => setProjectSearch(e.target.value)}
                                            />

                                            {groupedProjects.length === 0 && (
                                                <div className="al-dropdown-empty">No projects found for the selected localities</div>
                                            )}

                                            {groupedProjects.map((group) => {
                                                const filtered = group.projects.filter((p) =>
                                                    p.project_name.toLowerCase().includes(projectSearch.toLowerCase())
                                                );
                                                if (!filtered.length) return null;

                                                return (
                                                    <div key={group.id} className="al-dropdown-group">
                                                        <div className="al-dropdown-group-title">{group.name}</div>
                                                        {filtered.map((proj) => (
                                                            <label key={proj._id} className="al-dropdown-item">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={leadData.project_id.includes(proj._id)}
                                                                    onChange={() => toggleProject(proj._id)}
                                                                />
                                                                <span>{proj.project_name}</span>
                                                                {(proj.project_type?.name === "Residential" ||
                                                                    proj.project_type?.name === "Commercial") && (
                                                                        <span className="al-dropdown-hint">{proj.project_type.name}</span>
                                                                    )}
                                                            </label>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {errors.project_id && <span className="al-error-msg">{errors.project_id}</span>}
                            </div>

                            <div className="al-field al-field--full">
                                <label className="al-label">
                                    {leadData.lead_for === "Site_visit" ? "Pickup Address" : "Address"}
                                    {leadData.lead_for !== "Site_visit" && <span className="al-req">*</span>}
                                </label>

                                {loadError && (
                                    <div className="al-map-placeholder">
                                        <p>Map unavailable — check your Google Maps API key</p>
                                    </div>
                                )}

                                {!isLoaded && !loadError && (
                                    <div className="al-map-placeholder">
                                        <p>Loading map...</p>
                                    </div>
                                )}

                                {isLoaded && !loadError && (
                                    <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                                        <input
                                            className={`al-input ${errors.address ? "al-input--error" : ""}`}
                                            type="text"
                                            name="address"
                                            value={leadData.address.address}
                                            onChange={handleAddressChange}
                                            placeholder={leadData.lead_for === "Site_visit" ? "Search pickup location..." : "Search area or address..."}
                                            disabled={loading}
                                        />
                                    </Autocomplete>
                                )}

                                {errors.address && <span className="al-error-msg">{errors.address}</span>}

                                {isLoaded && !loadError && (
                                    <>
                                        <GoogleMap
                                            mapContainerStyle={mapContainerStyle}
                                            center={mapCenter}
                                            zoom={13}
                                            onClick={onMapClick}
                                        >
                                            {markerPosition && <Marker position={markerPosition} />}
                                        </GoogleMap>
                                        <p className="al-map-hint">Click on the map or search above to set the location</p>
                                    </>
                                )}
                            </div>

                            <div className="al-field">
                                <label className="al-label">House / Flat / Floor No. <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="house_flat_floorno"
                                    value={leadData.address.house_flat_floorno}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. #6676"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Apartment / Road / Area <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="appartment_road_area"
                                    value={leadData.address.appartment_road_area}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. Great India Place, Bhangel, Secor-18, Noida"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Direction to Reach <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="direction_to_reach"
                                    value={leadData.address.direction_to_reach}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. Near main gate"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Section 03: Lead Details ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">03</span>
                            <h2 className="al-section-title">Lead Details</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Lead Source <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.lead_source ? "al-input--error" : ""}`}
                                    name="lead_source"
                                    value={leadData.lead_source}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    {LEAD_SOURCES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                                {errors.lead_source && <span className="al-error-msg">{errors.lead_source}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Price Range <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.price_range ? "al-input--error" : ""}`}
                                    name="price_range"
                                    value={leadData.price_range}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select price range</option>
                                    {Array.isArray(agentProfitRanges) && agentProfitRanges.map((r) => (
                                        <option key={r._id} value={r._id}>
                                            {r.category} — {formatIndianPrice(r.min_price)} to {formatIndianPrice(r.max_price)}
                                        </option>
                                    ))}
                                </select>
                                {errors.price_range && <span className="al-error-msg">{errors.price_range}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">
                                    {leadData.lead_for === "Site_visit" ? "Pickup Date" : "Appointment Date"} <span className="al-req">*</span>
                                </label>
                                <input
                                    className={`al-input ${errors.appointment_date ? "al-input--error" : ""}`}
                                    type="date"
                                    name="appointment_date"
                                    min={getMinAppointmentDate()}
                                    value={leadData.appointment_date}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.appointment_date && <span className="al-error-msg">{errors.appointment_date}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">
                                    {leadData.lead_for === "Site_visit" ? "Pickup Time" : "Slot Start Time"} <span className="al-req">*</span>
                                </label>
                                <input
                                    className={`al-input ${errors.slot_start_time ? "al-input--error" : ""}`}
                                    type="time"
                                    name="slot_start_time"
                                    min="09:00"
                                    max="19:00"
                                    value={leadData.slot_start_time}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.slot_start_time && <span className="al-error-msg">{errors.slot_start_time}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Slot End Time <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.slot_end_time ? "al-input--error" : ""}`}
                                    type="time"
                                    name="slot_end_time"
                                    min="09:00"
                                    max="19:00"
                                    value={leadData.slot_end_time}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.slot_end_time && <span className="al-error-msg">{errors.slot_end_time}</span>}
                            </div>

                            <div className="al-field al-field--full">
                                <label className="al-label">Notes <span className="al-optional">(Optional)</span></label>
                                <div className="al-note-input-wrap">
                                    <input
                                        className="al-input"
                                        type="text"
                                        value={noteInput}
                                        onChange={(e) => setNoteInput(e.target.value)}
                                        onKeyDown={handleNoteKeyDown}
                                        placeholder="Type a note and press Enter"
                                        disabled={loading}
                                    />
                                    <button type="button" className="al-note-add-btn" onClick={handleAddNote} disabled={loading}>
                                        Add
                                    </button>
                                </div>
                                {leadData.note.length > 0 && (
                                    <div className="al-note-chips">
                                        {leadData.note.map((n, idx) => (
                                            <span key={idx} className="al-note-chip">
                                                {n}
                                                <button
                                                    type="button"
                                                    className="al-note-chip-remove"
                                                    onClick={() => handleRemoveNote(idx)}
                                                    disabled={loading}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Section 04: Site Visit Details (conditional) ── */}
                    {/* {leadData.lead_for === "Site_visit" && (
                        <div className="al-section">
                            <div className="al-section-header">
                                <span className="al-section-badge">04</span>
                                <h2 className="al-section-title">Site Visit Details</h2>
                            </div>

                            <div className="al-grid al-grid-2">
                                <div className="al-field">
                                    <label className="al-label">Visit By <span className="al-req">*</span></label>
                                    <select
                                        className="al-select"
                                        name="site_visit_by"
                                        value={leadData.site_visit_by}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {SITE_VISIT_BY.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="al-field al-field--full">
                                    <label className="al-label">Drop Address <span className="al-req">*</span></label>
                                    {isLoaded && !loadError && (
                                        <Autocomplete onLoad={onDropAutocompleteLoad} onPlaceChanged={onDropPlaceChanged}>
                                            <input
                                                className={`al-input ${errors.drop_address ? "al-input--error" : ""}`}
                                                type="text"
                                                name="drop_address"
                                                value={leadData.drop_address}
                                                onChange={handleChange}
                                                placeholder="Search drop location..."
                                                disabled={loading}
                                            />
                                        </Autocomplete>
                                    )}
                                    {errors.drop_address && <span className="al-error-msg">{errors.drop_address}</span>}
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* ── Footer ── */}
                    <div className="al-footer">
                        <Link to="/leads" className="al-btn al-btn--cancel" onClick={handleReset}>
                            Cancel
                        </Link>
                        <button type="submit" className="al-btn al-btn--submit" disabled={loading}>
                            {loading ? "Saving..." : "Save Lead"}
                        </button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default AddLead;